/**
 * GhostPropertyMapper
 *
 * E018-SOLID: Maps GhostDefinition to GhostBlockProps and provides
 * generic property diffing for reconciliation.
 *
 * This replaces the hardcoded property handling in the old GhostSyncService.
 */

import { serializeXYWH } from '@blocksuite/affine/global/gfx';

import { GHOST_HEIGHT, GHOST_WIDTH } from '../core/constants';
import type {
  GhostBlockPropsInput,
  IGhostPropertyMapper,
  Position,
} from '../core/interfaces/IGhostPropertyMapper';
import type { GhostDefinition } from '../core/types';

// =============================================================================
// Implementation
// =============================================================================

/**
 * Maps GhostDefinition to block properties and provides generic diffing.
 */
export class GhostPropertyMapper implements IGhostPropertyMapper {
  /**
   * Converts a GhostDefinition to block properties.
   */
  toBlockProps(
    definition: GhostDefinition,
    position: Position
  ): GhostBlockPropsInput {
    // Determine ghost type based on definition type
    // - automation-incoming = target is an external automation → ghostType: 'automation'
    // - automation-outgoing = target is an external database → ghostType: 'database'
    // - relation-* = target is always a database → ghostType: 'database'
    const isAutomationGhost = definition.type === 'automation-incoming';

    // Derive automationDirection from preferredSide for automation ghosts
    // 'right' = DB is input (source), 'left' = DB is output (target)
    const automationDirection: 'source' | 'target' | undefined =
      isAutomationGhost
        ? definition.preferredSide === 'right'
          ? 'source'
          : 'target'
        : undefined;

    return {
      xywh: serializeXYWH(position.x, position.y, GHOST_WIDTH, GHOST_HEIGHT),
      sourceDatabaseId: definition.sourceBlockId,
      sourceElementId: definition.sourceElementId,
      targetDatabaseId: definition.targetId,
      targetDocId: definition.targetDocId,
      targetDocTitle: definition.targetDocTitle,
      targetDatabaseTitle: definition.targetTitle,
      targetAccessState: definition.targetAccessState,
      ghostType: isAutomationGhost ? 'automation' : 'database',
      isTargetInTrash: definition.isTargetInTrash ?? false,
      automationDirection,
    };
  }

  /**
   * Compares current props with desired props and returns the diff.
   *
   * This is the key improvement over the old approach:
   * - Compares ALL relevant properties generically
   * - Returns only changed properties
   * - No hardcoding of specific property names
   */
  diff(
    current: Partial<GhostBlockPropsInput>,
    desired: GhostBlockPropsInput
  ): Partial<GhostBlockPropsInput> | null {
    const changes: Partial<GhostBlockPropsInput> = {};
    let hasChanges = false;

    // Compare each property
    // Using a list of keys to check ensures we don't miss any property
    const keysToCompare: (keyof GhostBlockPropsInput)[] = [
      'xywh',
      'sourceDatabaseId',
      'sourceElementId',
      'targetDatabaseId',
      'targetDocId',
      'targetDocTitle',
      'targetDatabaseTitle',
      'targetAccessState',
      'ghostType',
      'isTargetInTrash',
      'automationDirection',
    ];

    for (const key of keysToCompare) {
      const currentValue = current[key];
      const desiredValue = desired[key];

      // Skip undefined desired values (optional props)
      if (desiredValue === undefined) continue;

      // Check if values differ
      if (currentValue !== desiredValue) {
        // Type-safe assignment
        (changes as Record<string, unknown>)[key] = desiredValue;
        hasChanges = true;
      }
    }

    return hasChanges ? changes : null;
  }

  /**
   * Checks if any properties need updating.
   */
  needsUpdate(
    current: Partial<GhostBlockPropsInput>,
    desired: GhostBlockPropsInput
  ): boolean {
    return this.diff(current, desired) !== null;
  }
}
